import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import {CommonModule} from "@angular/common";
import { IrbCommonModule } from "@irb/irb-common/irb-common.module";
import { IPortfolioResultResponse, IPortfolioResultPFSECRebal, IPortfolioResultChartData, IPortfolioResultChartConfig, IACLevel } from '@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort, Sort} from '@angular/material/sort';
import {PrAllocationsService} from "@irb/irb-editors/editor-portfolio-result/services";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { UtilityService } from '@irb/irb-common/services/utility.service';

@Component({
  selector: 'irb-com-pr-allocation-chart-group',
  templateUrl: './com-pr-allocation-chart-group.component.html',
  styleUrl: './com-pr-allocation-chart-group.component.scss',
  standalone: true,
  imports: [CommonModule, IrbCommonModule],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ComPrAllocationChartGroupComponent implements OnInit, AfterViewInit {
  _data: IPortfolioResultResponse;
  _config: IPortfolioResultChartConfig;
  @Input() expanded: boolean;
  @Input() tabTitle: string;
  @Input() subTitle: string;
  @Input() groupBy: string;
  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() configChange = new EventEmitter<IPortfolioResultChartConfig>();
  chartDataPieBefore: any;
  chartDataPieAfter: any;
  chartDataBarBeforeAndAfter: any;
  dataGrouped: Array<IPortfolioResultChartData>;
  percentagesBefore: Array<number> = [];
  percentagesAfter: Array<number> = [];
  percentagesChange: Array<number> = [];
  tableDataSource: MatTableDataSource<IPortfolioResultChartData>;
  tableColumns = ['label', 'targetTotal', 'beforeTotal', 'color', 'percentageChange', 'afterTotal', 'colorSecondary'];
  sumPercentagesBefore: number;
  sumPercentagesAfter: number;
  barChartHeight: string;
  hasChildren: Function = (_i: number, row: any) => row.eqChildren?.length > 0;

  @ViewChild(MatSort) sort: MatSort;

  @Input()
  get data(): IPortfolioResultResponse {
    return this._data;
  }

  set data(d: IPortfolioResultResponse) {
    this._data = d;
    this.populateChartData();
  }

  @Input()
  get config(): IPortfolioResultChartConfig {
    return this._config;
  }

  set config(c: IPortfolioResultChartConfig) {
    this._config = c;
    this.populateChartData();
  }

  colors: Array<string> = ["#0061AF", "#652D90", "#008641", "#F15A22", "#BA006F", "#0083CA", "#CC99FF", "#9ACA3C", "#F58220", "#F5A4C7", "#7BAFDE", "#9B6DCC", "#3A7224", "#FED09E", "#00B6F1", "#6F60AA", "#DDE89A", "#FCAF17", "#8ED8F8", "#CBDB2A", "#FBB034", "#00ACD4", "#B5AF75", "#007B8D", "#669156", "#00B3B5", "#8C9B1B", "#ADE0EE", "#8FCEA5", "#9FCBED", "#00AE4D", "#72CDF4", "#78A22F", "#009DDC", "#8FCEA5", "#009AC7"];

  colorsSecondary: Array<string> = ["#669FCF", "#A575B0", "#66B881", "#F69A82", "#DA66BF", "#66B3EA", "#EEDDFF", "#DAEA9C", "#F9C280", "#F9D4E7", "#BBDFEE", "#CBADEC", "#9AB284", "#FEEADE", "#66D6F1", "#BFA0CA", "#F7F2DA", "#FCCF77", "#CEF8F8", "#EBFB8A", "#FBD094", "#66CCE4", "#D5CFB5", "#66ABBD", "#B6C1A6", "#66D3D5", "#DCDBB7", "#EDFAFF", "#CFEECD", "#DFEBFD", "#66CEAB", "#B2EDF4", "#C8D28F", "#66BDEC", "#CFEECD", "#66CAE7"];

  pieChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    cutoutPercentage: 50,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label;
            const value = context.dataset.data[context.dataIndex].toFixed(1);
            return label + " is " + value + "%";
          }
        }
      }
    }
  };

  plugins = [ChartDataLabels];
  barChartOptions: any = {
      indexAxis: 'y',
      responsive: false,
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      barPercentage: 0.8,
      layout: {
          padding: {
              right: 100
          }
      },
      plugins: {
          legend: {
              display: false
          },
          tooltip: {
              enabled: false
          },
          datalabels: {
              labels: {
                  primary: {
                      anchor: 'end',
                      align: 'left',
                      offset: -50,
                      display: true,
                      backgroundColor: "#EEE",
                      borderRadius: 2,
                      font: {size: 9},
                      formatter: (value: any, _context: any) => {
                          return value.toFixed(2) + "%";
                      }
                  }
              }
          }
      },
      scales: {
          x: {
              ticks: {
                  color: "#CCC",
                  font: {
                      weight: 500
                  }
              },
              grid: {
                  color: "#CCC",
                  drawBorder: false
              },
              position: 'top'
          },
          y: {
              grid: {
                  display: false
              }
          }
      }
  };

  constructor(public pralloc: PrAllocationsService, public util: UtilityService) {}

  ngOnInit() {
    this.populateChartData();
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  populateChartData() {
    let labels: Array<string> = [];
    this.dataGrouped = [];
    let colorIndex: number = 0;
    let colors: Array<string> = [];
    let colorsSecondary: Array<string> = [];
    this.percentagesBefore = [];
    this.percentagesAfter = [];
    this.percentagesChange = [];
    let showEquivalents = this.groupBy == "POSITION" && this.config?.showEquivalents;

    this.pralloc.getFilteredItemsForChart(this.util.clone(this.data), showEquivalents).forEach((r: IPortfolioResultPFSECRebal) => {
        if(this.groupBy == "SEC_CATEGORY") {          
          const f = this.dataGrouped.find((x: IPortfolioResultChartData) => x.group === r.secCatagory);

          if(f) {
            f.beforeTotal += r.secCurPercntVal;
            f.afterTotal += r.secPercntValAfterAdj;
            f.targetTotal += r.secModelTargetPercnt;            
            f.beforeTotal = (f.beforeTotal > 100) ? 100 : f.beforeTotal;
            f.afterTotal = (f.afterTotal > 100) ? 100 : f.afterTotal;
            f.percentageChange = Math.round((f.afterTotal - f.beforeTotal) * 100) / 100;
          } else {
            this.dataGrouped.push({
              beforeTotal: r.secCurPercntVal,
              afterTotal: r.secPercntValAfterAdj,
              targetTotal: r.secModelTargetPercnt,
              percentageChange: Math.round((r.secPercntValAfterAdj - r.secCurPercntVal) * 100) / 100,
              group: r.secCatagory,
              label: r.secCatagory
            } as IPortfolioResultChartData);
          }
        } else if(this.groupBy == "STYLE_BOX") {   
          const s = (r.styleBoxLabel) ? r.styleBoxLabel : "Uncategorized";       
          const f = this.dataGrouped.find((x: IPortfolioResultChartData) => x.group === s);

          if(f) {
            f.beforeTotal += r.secCurPercntVal;
            f.afterTotal += r.secPercntValAfterAdj;
            f.targetTotal += r.secModelTargetPercnt;
            f.beforeTotal = (f.beforeTotal > 100) ? 100 : f.beforeTotal;
            f.afterTotal = (f.afterTotal > 100) ? 100 : f.afterTotal;
            f.percentageChange = Math.round((f.afterTotal - f.beforeTotal) * 100) / 100;
          } else {
            this.dataGrouped.push({
              beforeTotal: r.secCurPercntVal,
              afterTotal: r.secPercntValAfterAdj,
              targetTotal: r.secModelTargetPercnt,
              percentageChange: Math.round((r.secPercntValAfterAdj - r.secCurPercntVal) * 100) / 100,
              group: s,
              label: s 
            } as IPortfolioResultChartData);
          }
        } else if(this.groupBy == "SEC_TYPE") {   
          const s = (r.sacSecurityType) ? r.sacSecurityType : "Other";       
          const f = this.dataGrouped.find((x: IPortfolioResultChartData) => x.group === s);

          if(f) {
            f.beforeTotal += r.secCurPercntVal;
            f.afterTotal += r.secPercntValAfterAdj;
            f.targetTotal += r.secModelTargetPercnt;
            f.beforeTotal = (f.beforeTotal > 100) ? 100 : f.beforeTotal;
            f.afterTotal = (f.afterTotal > 100) ? 100 : f.afterTotal;
            f.percentageChange = Math.round((f.afterTotal - f.beforeTotal) * 100) / 100;
          } else {
            this.dataGrouped.push({
              beforeTotal: r.secCurPercntVal,
              afterTotal: r.secPercntValAfterAdj,
              targetTotal: r.secModelTargetPercnt,
              percentageChange: Math.round((r.secPercntValAfterAdj - r.secCurPercntVal) * 100) / 100,
              group: s, 
              label: s 
            } as IPortfolioResultChartData);
          }
        } else if(this.groupBy == "SAC1" || this.groupBy == "SAC2" || this.groupBy == "SAC3" || this.groupBy == "SAC4") {
          let acLevel: Array<IACLevel>;

          if(this.groupBy == "SAC1") {
            acLevel = r.acLevel1;
          } else if(this.groupBy == "SAC2") {
            acLevel = r.acLevel2;
          } else if(this.groupBy == "SAC3") {
            acLevel = r.acLevel3;
          } else if(this.groupBy == "SAC4") {
            acLevel = r.acLevel4;
          }
          if (!acLevel)acLevel=[];

          if(acLevel.length == 0) {
            this.dataGrouped.push({
                beforeTotal: r.secCurPercntVal,
                afterTotal: r.secPercntValAfterAdj,
                targetTotal: r.secModelTargetPercnt,
                percentageChange: Math.round((r.secPercntValAfterAdj - r.secCurPercntVal) * 100) / 100,
                group: r.secSymb,
                label: r.secSymb
            } as IPortfolioResultChartData);
          } else {
            acLevel.forEach((a: IACLevel) => {
                const f = this.dataGrouped.find((x: IPortfolioResultChartData) => a.code.toString() === x.group);
                const allocation = a.allocation / 100;

                if(f) {
                    f.beforeTotal += r.secCurPercntVal * allocation;
                    f.afterTotal += r.secPercntValAfterAdj * allocation;
                    f.targetTotal += r.secModelTargetPercnt * allocation;
                    f.beforeTotal = (f.beforeTotal > 100) ? 100 : f.beforeTotal;
                    f.afterTotal = (f.afterTotal > 100) ? 100 : f.afterTotal;
                    f.percentageChange = Math.round((f.afterTotal - f.beforeTotal) * 100) / 100;
                } else {
                    this.dataGrouped.push({
                        beforeTotal: r.secCurPercntVal * allocation,
                        afterTotal: r.secPercntValAfterAdj * allocation,
                        targetTotal: r.secModelTargetPercnt * allocation,
                        percentageChange: Math.round(((r.secPercntValAfterAdj * allocation) - (r.secCurPercntVal * allocation)) * 100) / 100,
                        group: a.code.toString(),
                        label: a.label
                    } as IPortfolioResultChartData);
                }
            });
          }
        } else if(this.groupBy == "POSITION") {
          const f = this.dataGrouped.find((x: IPortfolioResultChartData) => x.group === r.secSymb);

          if(f) {
            f.beforeTotal += r.secCurPercntVal;
            f.afterTotal += r.secPercntValAfterAdj;
            f.targetTotal += r.secModelTargetPercnt;
            f.beforeTotal = (f.beforeTotal > 100) ? 100 : f.beforeTotal;
            f.afterTotal = (f.afterTotal > 100) ? 100 : f.afterTotal;
            f.percentageChange = Math.round((f.afterTotal - f.beforeTotal) * 100) / 100;
          } else {
            let eqChildren = (r.eqChildren) ? this.getEqChildren(r.eqChildren) : []

            this.dataGrouped.push({
              beforeTotal: r.secCurPercntVal,
              afterTotal: r.secPercntValAfterAdj,
              targetTotal: r.secModelTargetPercnt,
              percentageChange: Math.round((r.secPercntValAfterAdj - r.secCurPercntVal) * 100) / 100,
              group: ((r.secExclusionType) ? "* " : "") + r.secSymb,
              label: ((r.secExclusionType) ? "* " : "") + r.secSymb,
              eqChildren: eqChildren,
              showDetail: true
            } as IPortfolioResultChartData);
          }
        }
    });

    this.dataGrouped.sort((a: IPortfolioResultChartData, b: IPortfolioResultChartData) => a.label.localeCompare(b.label, 'en', {caseFirst: 'upper'}));

    while(this.colors.length < this.dataGrouped.length) {
      this.colors = this.colors.concat(this.colors);
      this.colorsSecondary = this.colorsSecondary.concat(this.colorsSecondary);
    }

    this.dataGrouped.forEach((r: IPortfolioResultChartData) => {      
      this.percentagesBefore.push(r.beforeTotal);
      this.percentagesAfter.push(r.afterTotal);
      this.percentagesChange.push(Math.round((r.afterTotal - r.beforeTotal) * 100) / 100);
      labels.push((r.label) ? r.label : r.group);   

      if(r.group === "Uncategorized" || r.group === "Other") {
        colors.push("#BABABA");
        colorsSecondary.push("#BABABA");
        r.color = colors[colors.length - 1];
        r.colorSecondary = colorsSecondary[colorsSecondary.length - 1];
      } else {
        colors.push(this.colors[colorIndex]);
        colorsSecondary.push(this.colorsSecondary[colorIndex]);
        r.color = this.colors[colorIndex];
        r.colorSecondary = this.colorsSecondary[colorIndex];
        colorIndex++;
      }
    });

    this.chartDataPieBefore = {
      labels: labels,
      datasets: [
        {
          data: this.percentagesBefore,
          backgroundColor: colors,
          hoverBackgroundColor: colors,
          borderWidth: 0,
          hoverBorderColor: ["#FFFFFF"]
        }
      ]
    };
        
    this.chartDataPieAfter = {
      labels: labels,
      datasets: [
        {
          data: this.percentagesAfter,
          backgroundColor: colorsSecondary,
          hoverBackgroundColor: colorsSecondary,
          borderWidth: 0,
          hoverBorderColor: ["#FFFFFF"]
        }
      ]
    };

    this.chartDataBarBeforeAndAfter = {
        labels: labels,
        datasets: [
            {
                label: 'Before Adjustments',
                backgroundColor: colors,
                borderColor: "#CCC",
                data: this.percentagesBefore
            },
            {
                label: 'After Adjustments',
                backgroundColor: colorsSecondary,
                borderColor: "#CCC",
                data: this.percentagesAfter,
                datalabels: {
                    labels: {
                        secondary: {
                            color: (_context: any) => {
                                return (this.percentagesChange[_context.dataIndex] > 0) ? 'green' : 'red';
                            },
                            anchor: 'end',
                            align: 'right',
                            offset: 48,
                            backgroundColor: "#EEE",
                            borderRadius: 2,
                            font: {size: 9},
                            formatter: (_value: any, _context: any) => {
                                if(this.percentagesChange[_context.dataIndex] > 0) {
                                    return "(+" + this.percentagesChange[_context.dataIndex].toFixed(2) + "%)";
                                } else if(this.percentagesChange[_context.dataIndex] < 0) {
                                    return "(" + this.percentagesChange[_context.dataIndex].toFixed(2) + "%)";
                                } 
                            },
                            display: (_context: any) => {
                                return this.percentagesChange[_context.dataIndex] !== 0;
                            }
                        }
                    }
                }
            }
        ]
    };

    this.barChartHeight = 40 + (this.percentagesBefore.length * 60) + "px";
    this.sumPercentagesBefore = Math.round(this.percentagesBefore.reduce((a: any, v: any) => a + v, 0) * 100 / 100);
    this.sumPercentagesAfter = Math.round(this.percentagesAfter.reduce((a: any, v: any) => a + v, 0) * 100 / 100);
    this.tableDataSource = new MatTableDataSource([...this.dataGrouped]);

    this.tableDataSource.sortData = (data: IPortfolioResultChartData[], sort: MatSort) => {
      const active = sort.active;
      const direction = sort.direction;

      if (!active || direction === '') {
        return data;
      }

      return data.sort((a: IPortfolioResultChartData, b: IPortfolioResultChartData) => a.label.localeCompare(b.label, 'en', {caseFirst: 'upper'}));
    };
    
    this.tableDataSource.sort = this.sort;
  }

  onExpandedChange() {
    if(this.expanded) {
      setTimeout(() => this.populateChartData());
    }

    this.expandedChange.emit(this.expanded);
  }

  onSortChange(sortState: Sort) {
    this.config.sortMap[this.groupBy] = {
      column: sortState.active,
      order: sortState.direction
    }

    this.configChange.emit(this.config);
  }

  getEqChildren(eqChildren: Array<IPortfolioResultPFSECRebal>): Array<IPortfolioResultChartData> {
    let c = [] as Array<IPortfolioResultChartData>;

    eqChildren.forEach((r: IPortfolioResultPFSECRebal) => {
      c.push({
        beforeTotal: r.secCurPercntVal,
        afterTotal: r.secPercntValAfterAdj,
        targetTotal: r.secModelTargetPercnt,
        percentageChange: Math.round((r.secPercntValAfterAdj - r.secCurPercntVal) * 100) / 100,
        group: ((r.secExclusionType) ? "* " : "") + r.secSymb,
        label: ((r.secExclusionType) ? "* " : "") + r.secSymb
      } as IPortfolioResultChartData);
    });

    return c;
  }

  getDataSource(eqChildren: Array<IPortfolioResultPFSECRebal>) {
    return new MatTableDataSource([...eqChildren]);
  }

  toggleShowDetail(row: IPortfolioResultChartData) {
    if(row.eqChildren?.length > 0) {
      row.showDetail = !row.showDetail;
    }
  }
}
